/* Isolated adapter: native controls, variant events, gallery and cart remain Empire-owned. */
(() => {
  if (customElements.get('mta-map-hoodie')) return;

  class MtaMapHoodie extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      const section = this.closest('.shopify-section');
      const data = section?.querySelector('[data-section-data]');
      if (!data) return;
      this.config = JSON.parse(data.textContent);
      this.selection = this.querySelector('[data-variant-selection]');
      this.options = this.querySelector('[data-options-selection]');
      this.master = this.querySelector('[data-variants]');
      this.form = this.querySelector('[data-product-form]');
      if (!this.selection || !this.master || !this.form) return;
      this.lifecycle = new AbortController();
      const signal = this.lifecycle.signal;
      this.version = 0;
      this.restoreVersion = 0;
      this.restoreTarget = null;
      this.restorePending = false;
      this.availabilityTimer = null;
      this.current = this.findVariant(this.config.mta.initial_variant_id);
      this.historyEnabled = !window.Shopify?.designMode;
      // Capture runs before Empire's target listener, independent of script load order.
      this.addEventListener('variant-change', event => this.onVariant(event), { capture: true, signal });
      this.form.addEventListener('submit', event => {
        const selected = this.findVariant(this.selection.getAttribute('variant'));
        if (this.restorePending || !selected?.available || this.master.disabled || String(this.master.value) !== String(selected.id)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showState(selected);
        }
      }, { capture: true, signal });
      window.addEventListener('popstate', () => this.restore(), { signal });
      section.addEventListener('shopify:section:unload', () => this.disconnect(), { signal });
      this.setupGalleryImages(signal);
      this.setupEditorialImages();
      this.showState(this.current);
      this.restore();
    }

    disconnectedCallback() { this.disconnect(); }

    disconnect() {
      this.lifecycle?.abort();
      this.lifecycle = null;
      this.editorialObserver?.disconnect();
      this.editorialObserver = null;
      clearTimeout(this.availabilityTimer);
      this.availabilityTimer = null;
      this.version += 1;
      this.restoreVersion += 1;
      this.restorePending = false;
    }

    setupEditorialImages() {
      const images = Array.from(this.querySelectorAll('img[data-mta-editorial-src]'));
      const reveal = image => {
        if (!image.hasAttribute('data-mta-editorial-src')) return;
        image.srcset = image.dataset.mtaEditorialSrcset || '';
        image.src = image.dataset.mtaEditorialSrc;
        image.removeAttribute('data-mta-editorial-src');
        image.removeAttribute('data-mta-editorial-srcset');
      };
      if (!('IntersectionObserver' in window)) {
        images.forEach(reveal);
        return;
      }
      this.editorialObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          this.editorialObserver?.unobserve(entry.target);
        });
      }, { rootMargin: '200px 0px' });
      images.forEach(image => this.editorialObserver.observe(image));
    }

    setupGalleryImages(signal) {
      const gallery = this.querySelector('[data-mta-preview-gallery]');
      if (!gallery) return;
      const revealSelected = () => {
        const image = gallery.querySelector('[data-gallery-figure][data-gallery-selected="true"] img[data-mta-gallery-src]');
        if (!image) return;
        image.srcset = image.dataset.mtaGallerySrcset || '';
        image.src = image.dataset.mtaGallerySrc;
        image.loading = 'eager';
        image.removeAttribute('data-mta-gallery-src');
        image.removeAttribute('data-mta-gallery-srcset');
      };
      // Empire owns slide/thumbnail/zoom selection and emits this after each change.
      gallery.addEventListener('mta:media-change', revealSelected, { signal });
      revealSelected();
    }

    findVariant(id) {
      return this.config.product.variants.find(variant => String(variant.id) === String(id)) || null;
    }

    color(variant) {
      const index = this.config.mta.color_option_index;
      return index >= 0 ? variant?.options[index] : null;
    }

    showState(variant) {
      this.toggleAttribute('data-mta-invalid', !variant);
      this.toggleAttribute('data-mta-unpurchasable', !variant?.available);
      this.dataset.mtaColor = this.color(variant) || '';
      this.master.disabled = !variant;
      const status = this.querySelector('[data-mta-status]');
      status.textContent = !variant ? this.config.context.product_unavailable
        : variant.available ? '' : this.config.context.product_sold_out;
      // Only guard the nonexistent-variant branch that historical Empire cannot handle.
      // Valid-variant ATC text and enablement remain with the native listener.
      if (!variant) {
        const atc = this.querySelector('[data-product-atc]');
        atc.disabled = true;
        atc.classList.add('disabled');
        const label = atc.querySelector('.atc-button--text');
        (label || atc).textContent = this.config.context.product_unavailable;
      }
    }

    selectNativeOptions(variant) {
      if (!this.options) return false;
      const controls = Array.from(this.options.querySelectorAll('[data-variant-option]'));
      if (controls.length !== variant.options.length) return false;
      const matches = controls.map(control => {
        const index = Number(control.dataset.variantOptionIndex);
        const value = variant.options[index];
        if (!Number.isInteger(index) || value === undefined) return null;
        if (control.matches('select')) {
          return Array.from(control.options).some(option => option.value === value)
            ? { control, value } : null;
        }
        const radios = Array.from(control.querySelectorAll('input[type="radio"][data-variant-option-value]'));
        const input = radios.find(radio => radio.value === value);
        return input ? { control, radios, input } : null;
      });
      if (matches.some(match => !match)) return false;

      // Set every native control before notifying OptionsSelection once. Empire's
      // setSelectedOptions radio path expects a nonexistent `values` property.
      matches.forEach(match => {
        if (match.input) match.radios.forEach(radio => { radio.checked = radio === match.input; });
        else match.control.value = match.value;
      });
      (matches[0].input || matches[0].control).dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    async restore() {
      const lifecycle = this.lifecycle;
      const restoreVersion = ++this.restoreVersion;
      clearTimeout(this.availabilityTimer);
      this.availabilityTimer = null;
      this.restorePending = true;
      await Promise.all([
        customElements.whenDefined('variant-selection'),
        customElements.whenDefined('options-selection'),
      ]);
      if (!lifecycle || lifecycle.signal.aborted || this.lifecycle !== lifecycle || restoreVersion !== this.restoreVersion) return;
      const requested = new URL(window.location.href).searchParams.get('variant');
      const variant = this.findVariant(requested) || this.findVariant(this.config.mta.fallback_variant_id || this.config.mta.initial_variant_id);
      if (!variant) {
        this.showState(null);
        return;
      }
      const alreadySelected = String(this.selection.getAttribute('variant')) === String(variant.id);
      this.restoreTarget = String(variant.id);
      if (!this.selectNativeOptions(variant)) {
        this.showState(null);
        return;
      }
      // Attribute changes feed Empire's own price, availability, media, payment
      // and cart consumers. A same-ID selection needs no duplicate native event.
      this.selection.variant = variant.id;
      this.writeUrl(variant, 'replace', window.location.href);
      if (alreadySelected) {
        const product = await this.selection.getProduct();
        if (lifecycle.signal.aborted || this.lifecycle !== lifecycle || restoreVersion !== this.restoreVersion) return;
        const pricing = this.querySelector('[data-product-pricing]');
        const atc = this.querySelector('[data-product-atc]');
        const needsRefresh = String(this.master.value) !== String(variant.id)
          || pricing?.style.visibility === 'hidden' || this.hasAttribute('data-mta-invalid')
          || (atc && atc.disabled !== !variant.available)
          || (variant.available && atc?.textContent.trim() === this.config.context.product_unavailable);
        if (needsRefresh && !product) {
          this.showState(null);
          return;
        }
        if (needsRefresh) {
          // A matching variant attribute emits no new event. Replay its native
          // consumer contract only when the master/price has actually drifted.
          this.selection.dispatchEvent(new CustomEvent('variant-change', {
            detail: { product, variant, state: 'selected' }
          }));
          this.repairNativeAvailability(variant, product, lifecycle, restoreVersion);
        } else {
          this.current = variant;
          this.showState(variant);
          this.restoreTarget = null;
          this.restorePending = false;
        }
      }
    }

    repairNativeAvailability(variant, product, lifecycle, restoreVersion) {
      // Empire registers its ProductDetails variant listener asynchronously after
      // its API script loads. The first same-ID replay may precede that listener.
      // Retry only while the native ATC still disagrees with the selected variant.
      let attempts = 0;
      const check = () => {
        if (lifecycle.signal.aborted || this.lifecycle !== lifecycle || restoreVersion !== this.restoreVersion
          || String(this.selection.getAttribute('variant')) !== String(variant.id)) return;
        const atc = this.querySelector('[data-product-atc]');
        const label = atc?.textContent.trim();
        const stale = String(this.master.value) !== String(variant.id)
          || atc?.disabled !== !variant.available
          || (variant.available && label === this.config.context.product_unavailable);
        if (!stale || attempts++ >= 30) return;
        this.selection.dispatchEvent(new CustomEvent('variant-change', {
          detail: { product, variant, state: 'selected' }
        }));
        this.availabilityTimer = setTimeout(check, 150);
      };
      this.availabilityTimer = setTimeout(check, 150);
    }

    onVariant(event) {
      if (event.target !== this.selection) return;
      const variant = event.detail?.variant || null;
      const id = variant ? String(variant.id) : event.detail?.state;
      // Ignore stale asynchronous native fetch results after a newer selection.
      if (id !== this.selection.getAttribute('variant')) {
        event.stopImmediatePropagation();
        return;
      }
      const lifecycle = this.lifecycle;
      const version = ++this.version;
      const href = window.location.href;
      const historyState = window.history.state;
      const previous = this.current;
      const restoring = this.restoreTarget === id;
      const mode = restoring ? 'replace' : (variant && String(previous?.id) !== String(variant.id) ? 'push' : 'replace');
      this.restoreTarget = null;
      const previousMedia = this.querySelector('[data-gallery-figure][data-gallery-selected="true"]')?.dataset.media;
      this.showState(variant);
      if (!variant) {
        // Do not send a null variant into Empire's unguarded variant.id access.
        event.stopImmediatePropagation();
        this.current = null;
        this.restorePending = false;
        return;
      }
      this.current = variant;
      queueMicrotask(() => {
        if (!lifecycle || lifecycle.signal.aborted || this.lifecycle !== lifecycle || version !== this.version) return;
        // Empire has completed its synchronous update. Repair its variant-only URL
        // replacement, keeping the previous history entry intact for deliberate changes.
        this.writeUrl(variant, mode, href, historyState);
        this.restorePending = false;
        if (this.config.settings?.mta_media_groups) return;
        const sameColor = previous && this.color(previous) === this.color(variant);
        let media = sameColor ? previousMedia : variant.featured_media?.id;
        if (!sameColor && !media && this.config.mta.color_option_index >= 0) {
          media = this.config.product.variants.find(item => this.color(item) === this.color(variant) && item.featured_media)?.featured_media.id;
        }
        // Drive the existing thumbnail controller; no custom slide/zoom state.
        const thumbnail = Array.from(this.querySelectorAll('[data-gallery-thumbnail]'))
          .find(item => String(item.dataset.media) === String(media));
        if (thumbnail && thumbnail.dataset.gallerySelected !== 'true') thumbnail.click();
      });
    }

    writeUrl(variant, mode, href, state = window.history.state) {
      if (!this.historyEnabled) return;
      const url = new URL(href);
      url.searchParams.set('variant', variant.id);
      if (mode === 'push' && url.href !== href) {
        window.history.replaceState(state, '', href);
        window.history.pushState(state, '', url.href);
      } else {
        window.history.replaceState(state, '', url.href);
      }
    }
  }
  customElements.define('mta-map-hoodie', MtaMapHoodie);
})();
